import axios from 'axios'
import store from '@/store'
import router from '@/router'
import { envConfig } from '@/utils/env-config'
import { readToken, ACCESS_TOKEN_KEY, REFRESH_TOKEN_KEY } from '@/utils/token-storage'
import { acceptRefreshedToken } from '@/utils/session-hygiene'
import { buildChangePasswordRequest } from '@/utils/password-change'
import { createMemberGrantsApi } from '@/utils/member-grants-core'

const BASE_URL = envConfig.VUE_APP_API_BASE_URL || envConfig.VITE_API_BASE_URL
// Clean up API_VERSION - remove quotes, whitespace, and treat "v1" as empty for now
let API_VERSION = envConfig.VUE_APP_API_VERSION || envConfig.VITE_API_VERSION || ''
// Remove any quotes and whitespace, and ignore common incorrect values
API_VERSION = API_VERSION.replace(/['"]/g, '').trim()
if (API_VERSION === 'v1' || API_VERSION === '/v1' || API_VERSION === 'api/v1') {
  console.warn('Ignoring incorrect API_VERSION value:', API_VERSION)
  API_VERSION = ''
}

const authApi = axios.create({
  baseURL: API_VERSION ? `${BASE_URL}/api/${API_VERSION}` : `${BASE_URL}/api`,
  timeout: envConfig.VUE_APP_API_TIMEOUT || envConfig.VITE_API_TIMEOUT,
  headers: {
    'Content-Type': 'application/json'
  }
})

// Request interceptor
authApi.interceptors.request.use(
  (config) => {
    const token = store.state.auth?.accessToken || readToken(ACCESS_TOKEN_KEY)
    
    // Add token to all authenticated endpoints except login/registration/password reset
    const unauthenticatedEndpoints = ['/auth/auth', '/auth/registration', '/auth/reset-password', '/auth/set-password', '/auth/activate']
    const shouldAddToken = token && !unauthenticatedEndpoints.some(endpoint => config.url?.includes(endpoint))
    
    if (shouldAddToken) {
      config.headers.Authorization = `Bearer ${token}`
      // Debug log for profile update requests
      if (config.url?.includes('/auth/update-profile')) {
        console.log('Profile update request with token:', !!token, 'URL:', config.url)
      }
    }
    
    return config
  },
  (error) => {
    console.error('Request error:', error)
    return Promise.reject(error)
  }
)

// Track refresh state to prevent multiple concurrent refresh attempts
let isRefreshing = false
let failedQueue = []

// Process failed queue after successful refresh
const processQueue = (error, token = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error)
    } else {
      resolve(token)
    }
  })

  failedQueue = []
}

// Response interceptor
authApi.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config

    // Only handle 401 responses that haven't been retried
    if (error.response?.status === 401 && !originalRequest._retry) {
      // If already refreshing, queue this request
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject })
        }).then(token => {
          originalRequest.headers.Authorization = `Bearer ${token}`
          return authApi(originalRequest)
        }).catch(err => {
          return Promise.reject(err)
        })
      }

      originalRequest._retry = true
      isRefreshing = true

      const refreshToken = store.state.auth?.refreshToken || readToken(REFRESH_TOKEN_KEY)

      if (!refreshToken) {
        processQueue(error, null)
        isRefreshing = false
        store.dispatch('auth/logout')
        store.commit('setShowAuthModal', true)
        return Promise.reject(error)
      }

      try {
        // Create new axios instance to avoid interceptor loops
        const refreshInstance = axios.create({
          baseURL: authApi.defaults.baseURL,
          timeout: authApi.defaults.timeout
        })

        const response = await refreshInstance.post('/auth/token/refresh', { refreshToken })

        if (response.data?.success && response.data?.data?.access_token) {
          const newTokens = {
            accessToken: response.data.data.access_token,
            refreshToken: response.data.data.refresh_token || refreshToken,
            tokenValidSeconds: response.data.data.token_valid_seconds
          }

          // The refresh token above may have come from the `readToken`
          // fallback, which is not bound to the signed-in user — so the token
          // we just minted might belong to a PREVIOUS, possibly privileged
          // session. Adopting it would write it into storage and into the
          // same-origin `cs_access_token` cookie, handing the dashboard a
          // different identity than the one the customer signed in as. Refuse
          // a definite mismatch and sign out instead.
          const verdict = acceptRefreshedToken({
            sessionToken: store.state.auth?.accessToken,
            sessionUser: store.state.auth?.user,
            newToken: newTokens.accessToken
          })
          if (!verdict.ok) {
            console.warn('Refresh refused — token is not this session:', verdict.reason)
            processQueue(error, null)
            store.dispatch('auth/logout')
            store.commit('setShowAuthModal', true)
            return Promise.reject(error)
          }

          // Update stored tokens
          await store.dispatch('auth/updateTokens', newTokens)

          // Process queued requests
          processQueue(null, newTokens.accessToken)

          // Retry original request
          originalRequest.headers.Authorization = `Bearer ${newTokens.accessToken}`
          return authApi(originalRequest)
        } else {
          throw new Error('Invalid refresh response format')
        }
      } catch (refreshError) {
        console.error('Token refresh failed:', refreshError)
        processQueue(refreshError, null)
        store.dispatch('auth/logout')
        store.commit('setShowAuthModal', true)
        return Promise.reject(refreshError)
      } finally {
        isRefreshing = false
      }
    }

    return Promise.reject(error)
  }
)

// Auth API methods
export const authService = {
  // Authentication - matches Swagger: POST /api/auth/auth
  async login(credentials) {
    // Transform email to username for API compatibility
    const requestData = {
      username: credentials.email || credentials.username,
      password: credentials.password
    }
    const response = await authApi.post('/auth/auth', requestData)
    return response.data
  },

  // User registration - matches Swagger API
  //
  // Signup sends an EMAIL AND NOTHING ELSE (2026-10-19). It deliberately does
  // not send a password: auth-api creates the account with no password at all
  // and the only password the account ever has is the one set at activation
  // (`activateAccount` below → POST /auth/set-password). Sending one here
  // would be collecting a credential that is superseded minutes later, under
  // a different policy — which is the defect this replaced.
  //
  // Do NOT "fix" a caller by generating a throwaway password client-side: an
  // account created without one cannot be signed into, and that is the point.
  //
  // Phase G: user_type is a tier (STARTER/PRO/BUSINESS) and defaults to
  // STARTER on the backend. PERSONAL/COMPANY moves to org draft (not in v1
  // for starter tier — they get the system-managed default org).
  async register(userData) {
    const requestData = {
      username: userData.email
    }

    // TODO (Phase G follow-up): when backend exposes optional userType for
    // tier signup, accept it here. For now we omit it so the backend default
    // (STARTER) applies. The previous PERSONAL/COMPANY mapping is dropped.
    if (userData.userType) {
      requestData.userType = userData.userType
    }

    // Profile fields (first/last name, company) are NOT accepted by
    // POST /auth/registration and are no longer collected at signup — a form
    // that asks for data the endpoint drops is its own defect. They are
    // editable from the profile page after activation (`updateUserProfile`).

    const response = await authApi.post('/auth/registration', requestData)
    return response.data
  },

  async logout() {
    // No-op. There is no server-side logout endpoint. Client state cleanup
    // happens in the `auth/logout` Vuex action (LOGOUT mutation clears both
    // localStorage and sessionStorage tokens and resets the remember flag).
    // This method exists only so existing callers compile; it MUST NOT
    // dispatch back to the store, which would recurse infinitely with the
    // Vuex action that calls this method.
  },

  async refreshToken(refreshToken) {
    const response = await authApi.post('/auth/token/refresh', { refreshToken })
    return response.data
  },

  // User management
  async getUserProfile(username) {
    // Use the correct endpoint: POST /api/auth/get-user
    // Requires username in request body and Bearer token
    const response = await authApi.post('/auth/get-user', { username })
    return response.data
  },

  async updateUserProfile(profileData) {
    // Use the correct endpoint from Swagger: /api/auth/update-profile
    // Transform the data to match API expectations
    const requestData = {
      firstName: profileData.firstName,
      lastName: profileData.lastName,
      username: profileData.email || profileData.username, // API expects username
      birthDay: profileData.birthDate || profileData.birthDay, // Map birthDate to birthDay
      phone: profileData.phone || profileData.mobile // Include phone/mobile field
    }
    
    // Remove undefined/null fields to avoid API validation errors
    Object.keys(requestData).forEach(key => {
      if (requestData[key] === undefined || requestData[key] === null || requestData[key] === '') {
        delete requestData[key]
      }
    })
    
    const response = await authApi.post('/auth/update-profile', requestData)
    return response.data
  },

  // Change password (authenticated user)
  // Canonical endpoint: POST /api/auth/password/change (auth-api, @token_required).
  // The body is snake_case and carries no username - auth-api derives the user
  // from the bearer token's user_id. See src/utils/password-change.js.
  async changePassword(passwordData) {
    const response = await authApi.post('/auth/password/change', buildChangePasswordRequest(passwordData))
    return response.data
  },

  // Password reset - matches Swagger API
  async requestPasswordReset(emailOrData) {
    // Support both old format (string email) and new format (object with captcha)
    let requestData
    
    if (typeof emailOrData === 'string') {
      // Legacy format - just email as username
      requestData = { username: emailOrData }
    } else {
      // New format - API expects only username field for now
      requestData = { 
        username: emailOrData.email
        // Note: Captcha and other fields might not be supported by current API
        // Only send username to avoid validation errors
      }
    }
    
    const response = await authApi.post('/auth/reset-password', requestData)
    return response.data
  },

  // Set new password after reset - matches Swagger API
  async resetPassword(resetData) {
    // API expects: { hash, password, username }
    // resetData should contain: { token/hash, newPassword, email/username }
    const requestData = {
      hash: resetData.token || resetData.hash,
      password: resetData.newPassword || resetData.password,
      username: resetData.email || resetData.username
    }
    
    const response = await authApi.post('/auth/set-password', requestData)
    return response.data
  },

  // Account activation - completes registration by setting initial password
  // Backend endpoint: POST /api/auth/set-password (same flow used for first-login)
  // Returns { status, message } — no auth tokens. User must log in afterwards.
  async activateAccount(email, hash, password) {
    const response = await authApi.post('/auth/set-password', {
      username: email,
      hash,
      password
    })
    return response.data
  },

  // The ONE password policy, read from the service that enforces it.
  // auth-api's `PASSWORD_STRENGTH` (default `medium`) decides it, and
  // `POST /auth/set-password` validates against it — so the activation page
  // renders these words rather than keeping a second copy of the rules that
  // can drift out of agreement (it did: signup advertised "Min. 8
  // characters" while the server wanted 6 plus a special symbol and a digit).
  // Returns `{level, description, min_length, requirements[]}` or null; the
  // caller falls back to a stated default and lets the server refuse.
  async getPasswordRequirements() {
    const response = await authApi.get('/auth/password-requirements')
    const body = response.data
    return body?.data ?? body ?? null
  },

  async resendActivation(email) {
    // Reuses the password reset flow which generates a fresh activation hash
    // and emails the user a new link.
    const response = await authApi.post('/auth/reset-password', { username: email })
    return response.data
  },

  // Get current authenticated user with default org + allocation summary.
  // Token is attached automatically by the request interceptor.
  // Returns the unwrapped `data` field on success, throws on error.
  async getMe() {
    const response = await authApi.get('/auth/me')
    if (response.data?.success === false) {
      throw new Error(response.data?.message || 'Failed to load user info')
    }
    return response.data?.data ?? response.data
  },

  /**
   * Record the user's acceptance of the current trial terms (W4).
   *
   * auth-api is the source of truth for acceptance (contract A5): it stores
   * `{ accepted_plan_version, accepted_at }` against the user. We post the
   * `plan_version` lifted from the plan response so the acceptance is pinned
   * to the exact terms the user saw.
   *
   * BEST-EFFORT BY DESIGN: this MUST NOT block signup/upgrade. The
   * acceptance endpoint is not live yet (auth-api ships it on a parallel
   * branch — see contract integration note #1), so a 404/501/network error
   * is swallowed and reported in the resolved value rather than thrown. The
   * auth-api side will expose it under POST /api/auth/trial-terms/accept;
   * the tier-management surface lives under POST /api/auth/users/tier/...
   *
   * @param {Object} params
   * @param {number|string|null} [params.planVersion] plan_version from the
   *        trial-enabled plan; sent as `plan_version`. May be null when the
   *        trial block isn't live yet — the call is still attempted so the
   *        backend can default it.
   * @returns {Promise<{ accepted: boolean, skipped?: boolean, data?: any,
   *          error?: string }>} never rejects.
   */
  async acceptTrialTerms({ planVersion } = {}) {
    try {
      const response = await authApi.post('/auth/trial-terms/accept', {
        plan_version: planVersion ?? null
      })
      if (response.data?.success === false) {
        return { accepted: false, error: response.data?.message || 'Acceptance rejected' }
      }
      return { accepted: true, data: response.data?.data ?? response.data }
    } catch (error) {
      // Endpoint not live yet (404/501) or transient failure — log + continue.
      // Signup/upgrade must never be blocked on trial-terms recording.
      console.warn('acceptTrialTerms (best-effort) failed:', error?.response?.status || error?.message || error)
      return { accepted: false, skipped: true, error: error?.message || 'unavailable' }
    }
  },

  // ----- Phase G v1.1 Sprint 2 — Plan-buyer signup wizard -----

  /**
   * Create a draft (pending-activation) organization tied to a plan.
   * The backend returns the new org id and subscription id; the wizard
   * stores both in component state for the awaiting-activation step.
   *
   * @param {Object} params
   * @param {number} params.plan_id
   * @param {Object} params.plan_snapshot — frozen copy of the chosen plan
   *        (resources + price) so future plan edits don't change history.
   * @param {Object} params.organization — { name, entity_type, vat_number,
   *        address, city, country, phone_number }
   */
  async createDraftOrganization({ plan_id, plan_snapshot, organization }) {
    const response = await authApi.post('/auth/organizations/draft', {
      plan_id,
      plan_snapshot,
      organization
    })
    if (response.data?.success === false) {
      throw new Error(response.data?.message || 'Failed to create draft organization')
    }
    return response.data?.data ?? response.data
  },

  /** List the current user's subscriptions. */
  async getSubscriptions() {
    const response = await authApi.get('/auth/subscriptions')
    if (response.data?.success === false) {
      throw new Error(response.data?.message || 'Failed to load subscriptions')
    }
    return response.data?.data ?? response.data
  },

  /** Get a single subscription. Used to poll for activation status. */
  async getSubscription(subscriptionId) {
    const response = await authApi.get(`/auth/subscriptions/${subscriptionId}`)
    if (response.data?.success === false) {
      throw new Error(response.data?.message || 'Failed to load subscription')
    }
    return response.data?.data ?? response.data
  },

  /**
   * Switch the active organization. Backend re-issues the JWT with the new
   * `current_organization_id` claim. The wizard calls this once the
   * subscription becomes active so the user lands in the new workspace org
   * instead of their starter membership org.
   *
   * Note: this only returns the API payload — the auth store update happens
   * in the auth Vuex action `switchOrganization`.
   */
  /**
   * The caller's OWN explicit permission grants — `GET /api/auth/permissions/
   * me/grants[?organization_id=]` (no admin authority needed; explicit
   * `user_resource_permissions` rows only, no role merge). Used by the
   * Certificates tab to detect the grantable `certificate.issue`
   * (Unified Certificate Service A-17) through
   * `hasCertificateIssueGrant()` in utils/certs-issue-core.js. Returns the
   * `grants` array; an empty array on any failure (fail closed).
   */
  async listMyGrants(organizationId = null) {
    const params = {}
    if (organizationId !== null && organizationId !== undefined && organizationId !== '') params.organization_id = organizationId
    const response = await authApi.get('/auth/permissions/me/grants', { params })
    const data = response.data?.data ?? response.data
    const grants = data && Array.isArray(data.grants) ? data.grants : (Array.isArray(data) ? data : [])
    return grants
  },

  async switchOrganization(organizationId) {
    const response = await authApi.post('/auth/user/switch-organization', {
      organization_id: organizationId
    })
    if (response.data?.success === false) {
      throw new Error(response.data?.message || 'Failed to switch organization')
    }
    return response.data?.data ?? response.data
  },

  /**
   * Resolve an invitation token to its target email + org metadata.
   * Public endpoint — the token itself is the gate. Used by the home
   * page to decide whether to open the login modal (existing user) or
   * the register modal (new user) when an unauthenticated visitor
   * arrives via an invitation email link.
   *
   * Returns null on 404 / network failure so callers can degrade
   * gracefully (fall back to the register modal).
   */
  async lookupInvitation(token) {
    if (!token) return null
    try {
      const response = await authApi.get('/auth/invitations/lookup', {
        params: { token }
      })
      if (response.data?.success === false) return null
      return response.data?.data ?? null
    } catch (e) {
      return null
    }
  },

  /**
   * Pin an org as the user's default. The pinned org is used as the
   * active context on next login (and as the /me fallback if the JWT
   * has no current_organization_id claim). Does NOT switch the
   * current session — combine with switchOrganization for that.
   */
  async setDefaultOrganization(organizationId) {
    const response = await authApi.post('/auth/user/default-organization', {
      organization_id: organizationId
    })
    if (response.data?.success === false) {
      throw new Error(response.data?.message || 'Failed to set default organization')
    }
    return response.data?.data ?? response.data
  },

  // ----- Phase G v1.2 — Member invitations -----

  /**
   * Invite a new member to an organization. Owners/admins only.
   * @param {string|number} orgId — target organization id
   * @param {Object} payload
   * @param {string} payload.email — invitee email
   * @param {string} payload.role — role to grant (e.g. ADMIN, MEMBER)
   * @param {Object} [payload.grantedResources] — optional per-member quota
   *        carve-out from the org's allocation pool. Backend may ignore if
   *        the tier doesn't support per-member allocation; future v1.3
   *        editor will let owners tune this from /admin/members.
   */
  async inviteMember(orgId, { email, role, grantedResources } = {}) {
    const body = { email, role }
    if (grantedResources) body.granted_resources = grantedResources
    const response = await authApi.post(`/auth/organizations/${orgId}/invitations`, body)
    if (response.data?.success === false) {
      throw new Error(response.data?.message || 'Failed to send invitation')
    }
    return response.data?.data ?? response.data
  },

  /**
   * List the current user's pending invitations across both source paths.
   * Backend returns a unified list; each item carries a `source` field that
   * must be echoed back on accept/reject so the server knows which collection
   * the invitation lives in.
   */
  async getPendingInvitations() {
    const response = await authApi.get('/auth/invitations/pending')
    if (response.data?.success === false) {
      throw new Error(response.data?.message || 'Failed to load pending invitations')
    }
    const data = response.data?.data ?? response.data
    // Backend returns `{ items: [...] }` (see auth-api app.py
    // get_pending_invitations_for_user). Accept legacy `invitations`
    // and a bare array shape too so the contract is forgiving.
    if (Array.isArray(data)) return data
    return data?.items || data?.invitations || []
  },

  /** Accept a pending invitation. `source` is required and must mirror the
   *  value the list endpoint returned for this item. */
  async acceptInvitation(id, source) {
    const response = await authApi.post(`/auth/invitations/${id}/accept`, { source })
    if (response.data?.success === false) {
      throw new Error(response.data?.message || 'Failed to accept invitation')
    }
    return response.data?.data ?? response.data
  },

  /** Reject (or cancel, when called by an org admin against an outbound
   *  invite) a pending invitation. `source` follows the same rule as accept. */
  async rejectInvitation(id, source) {
    const response = await authApi.post(`/auth/invitations/${id}/reject`, { source })
    if (response.data?.success === false) {
      throw new Error(response.data?.message || 'Failed to reject invitation')
    }
    return response.data?.data ?? response.data
  },

  /**
   * Fetch the members + outstanding invitations for an organization.
   * Maps to the legacy `get_members_and_invitations` route on auth-api;
   * if the deployed backend doesn't expose it under this path yet, callers
   * should fall back to a single-self list and surface a TODO banner.
   */
  async getOrganizationMembers(orgId) {
    const response = await authApi.get(`/auth/organizations/${orgId}/members`)
    if (response.data?.success === false) {
      throw new Error(response.data?.message || 'Failed to load members')
    }
    return response.data?.data ?? response.data
  },

  /**
   * Remove a member from an organization (Phase G v1.2).
   * Owner|admin can remove any non-owner. A user can also remove
   * themselves (self-leave). The org owner can never be removed
   * until ownership is transferred.
   */
  async removeMember(orgId, memberUserId) {
    const response = await authApi.delete(
      `/auth/organization/${orgId}/members/${memberUserId}`
    )
    if (response.data?.success === false) {
      throw new Error(response.data?.message || 'Failed to remove member')
    }
    return response.data?.data ?? response.data
  },

  // ----- Org-admin grants — domains, DNS records, certificates -------
  // (owner decision 2026-09-24; the Members page "Access" panel).
  // All three ride the EXISTING org-admin grant endpoints; the logic lives
  // in utils/member-grants-core.js so `node --test` can drive it.

  /** The org-admin grant client bound to the authenticated axios instance. */
  memberGrants: createMemberGrantsApi(authApi)
}

export default authApi